import { computed, type Ref } from 'vue';
import { getTransactionReferenceDetails } from '@peanuds/shared';
import type { Transaction } from '@peanuds/shared-state';

import {
  mapTransactionToTableRow,
  type ControlSpendingTableRow,
} from '../internal/control-spendings-table.adapter';
import {
  appendControlSpendingSearchText,
  filterControlSpendingTableRows,
} from '../internal/control-spendings-table.filters';
import type { ControlSpendingsTableFilter } from '../internal/control-spendings-table.types';

type UseControlSpendingsTableRowsOptions = {
  transactions: Ref<readonly Transaction[]>;
  cardRows: Ref<readonly ControlSpendingTableRow[]>;
  filter: () => ControlSpendingsTableFilter;
  search: () => string;
};

export const useControlSpendingsTableRows = ({
  transactions,
  cardRows,
  filter,
  search,
}: UseControlSpendingsTableRowsOptions) => {
  const rows = computed(() => {
    const transferRows = transactions.value.map(mapTransactionToTableRow);
    const tableRows =
      cardRows.value.length === 0
        ? transferRows
        : [...transferRows, ...cardRows.value].sort((a, b) =>
            (b.dateAndTime ?? '').localeCompare(a.dateAndTime ?? ''),
          );

    return tableRows.map((row) => appendControlSpendingSearchText(row));
  });

  const filteredRows = computed(() => {
    return filterControlSpendingTableRows(rows.value, {
      filter: filter(),
      search: search(),
    });
  });

  const totalRecords = computed(() => filteredRows.value.length);

  const hasReferenceDetails = computed(() => {
    return filteredRows.value.some((row) => getTransactionReferenceDetails(row).length > 0);
  });

  return {
    rows,
    filteredRows,
    totalRecords,
    hasReferenceDetails,
  };
};
